import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIonLoading } from '@ionic/react';
import useAPITrima from 'hooks/api/trima/useAPITrima';
import TrimaProgressHeader from 'pages/questionnaire-trima/components/TrimaProgressHeader';
import TrimaCadranSelection from 'pages/questionnaire-trima/slides/cadran/TrimaCadranSelection';
import {
  TrimaCadranMainWrapper,
  TrimaCadranStickyMini,
  TrimaCadranExpandedHeader,
} from 'pages/questionnaire-trima/slides/cadran/TrimaCadranMain.styled';
import { trimaStorage } from 'helpers/trima/trimaStorage';
import {
  TrimaCadranPayload,
  TrimaCadranNumber,
  TrimaPersistedState,
} from 'pages/questionnaire-trima/types/trima.types';
import { TRIMA_TOTAL_CADRANS } from 'pages/questionnaire-trima/constants/trima.constants';

export interface TrimaCadranMainProps {
  initialPayload: TrimaCadranPayload;
  initialState?: TrimaPersistedState | null;
  onComplete: () => void;
  onError?: () => void;
}

const TrimaCadranMain: React.FC<TrimaCadranMainProps> = ({
  initialPayload,
  initialState,
  onComplete,
  onError,
}) => {
  const { t } = useTranslation('trima');
  const { postCadran } = useAPITrima();
  const [present, dismiss] = useIonLoading();

  const [currentPayload, setCurrentPayload] = useState<TrimaCadranPayload>(initialPayload);
  const [showMini, setShowMini] = useState(false);
  const expandedRef = useRef<HTMLDivElement | null>(null);

  const [passageNumber, setPassageNumber] = useState<number>(initialState?.passageNumber ?? 1);
  const completedCadransRef = useRef<TrimaCadranNumber[]>(
    initialState?.completedCadrans ?? [],
  );
  const currentSelectionsRef = useRef<number[]>(initialState?.currentSelections ?? []);
  const sessionCompetenceIdRef = useRef<number>(
    initialState?.sessionCompetenceId ?? initialPayload.sessionCompetenceId,
  );

  const persist = () => {
    trimaStorage.save({
      currentCadran: currentPayload.cadranNumber,
      currentSelections: currentSelectionsRef.current,
      passageNumber,
      completedCadrans: completedCadransRef.current,
      sessionCompetenceId: sessionCompetenceIdRef.current,
      savedAt: Date.now(),
    });
  };

  // ---- Observe expanded header visibility via IntersectionObserver ----
  // We toggle the mini bar when the expanded block has scrolled (mostly) out
  // of view. Because both headers stay in the DOM and the sticky mini has a
  // fixed height, swapping their visibility cannot itself move the scroll
  // position — no feedback loop, no flicker.
  useEffect(() => {
    const target = expandedRef.current;
    if (!target) return;

    // IntersectionObserver works fine with the default viewport root for
    // ion-content in most setups; if your ion-content is the scroller, the
    // viewport root still observes correctly since the element scrolls with it.
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Mini appears when the expanded block has left the viewport.
        // Using only `isIntersecting` (default threshold 0) gives a single,
        // unambiguous flip — no hysteresis needed.
        setShowMini(!entry.isIntersecting);
      },
      { threshold: 0 },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [currentPayload.cadranNumber, passageNumber]);

  // Persist + scroll to top on every cadran/pass transition.
  useEffect(() => {
    persist();
    (document.querySelector('ion-content') as HTMLIonContentElement | null)?.scrollToTop(0);
    setShowMini(false);
  }, [passageNumber, currentPayload.cadranNumber]);

  const handleSelectionsChange = (selected: number[]) => {
    currentSelectionsRef.current = selected;
    persist();
  };

  const handleValidate = async (selected: number[]) => {
    currentSelectionsRef.current = selected;

    if (selected.length > currentPayload.minSelections) {
      const refinedItems = currentPayload.items.filter((item) =>
        selected.includes(item.id),
      );
      setCurrentPayload((prev) => ({ ...prev, items: refinedItems }));
      setPassageNumber((p) => p + 1);
      return;
    }

    if (selected.length < currentPayload.minSelections) return;

    await present({ spinner: 'circles', message: t('cadran.loading-next') });

    const response = await postCadran({
      cadranNumber: currentPayload.cadranNumber,
      selectedItemIds: selected,
      sessionCompetenceId: sessionCompetenceIdRef.current,
    });
    await dismiss();

    if (!response) {
      onError?.();
      return;
    }

    if (response.status === 'next') {
      completedCadransRef.current = [
        ...completedCadransRef.current,
        currentPayload.cadranNumber,
      ];
      currentSelectionsRef.current = [];
      sessionCompetenceIdRef.current = response.payload.sessionCompetenceId;
      setPassageNumber(1);
      setCurrentPayload(response.payload);
      return;
    }

    if (response.status === 'done') {
      trimaStorage.clear();
      onComplete();
    }
  };

  const currentStep = 2;
  const completedCadranCount = completedCadransRef.current.length;
  const progressPercent = 20 + (completedCadranCount / TRIMA_TOTAL_CADRANS) * 80;

  const instructionBase =
    passageNumber === 1
      ? `cadran.instruction-first-pass-${currentPayload.cadranNumber}`
      : `cadran.instruction-refine-${currentPayload.cadranNumber}`;

  const instructionParams = { count: currentPayload.items.length, min: currentPayload.minSelections };
  const instructionLead = t(`${instructionBase}-lead`, instructionParams);
  const instructionBold = t(`${instructionBase}-bold`, instructionParams);

  const counterLabel = t('cadran.counter', {
    current: currentPayload.cadranNumber,
    total: TRIMA_TOTAL_CADRANS,
  });

  return (
    <TrimaCadranMainWrapper>
      {/* Sticky mini-bar: always mounted, fixed height, fades in/out. */}
      <TrimaCadranStickyMini data-visible={showMini} aria-hidden={!showMini}>
        <span className="trima-mini-counter">{counterLabel}</span>
        <span className="trima-mini-instruction">
          {instructionLead} {instructionBold}
        </span>
      </TrimaCadranStickyMini>

      {/* Expanded header: in normal flow, scrolls away naturally. */}
      <TrimaCadranExpandedHeader ref={expandedRef}>
        <TrimaProgressHeader
          currentStep={currentStep}
          progressPercent={progressPercent}
          localCounterLabel={counterLabel}
        />
        <p className="trima-cadran-instruction">
          <span>{instructionLead}</span>
          <strong>{instructionBold}</strong>
        </p>
      </TrimaCadranExpandedHeader>

      <TrimaCadranSelection
        key={`${currentPayload.cadranNumber}-${passageNumber}`}
        items={currentPayload.items}
        minSelections={currentPayload.minSelections}
        isRefinement={passageNumber > 1}
        initialSelections={undefined}
        onSelectionsChange={handleSelectionsChange}
        onValidate={handleValidate}
      />
    </TrimaCadranMainWrapper>
  );
};

export default TrimaCadranMain;